import { Link, useLocation } from "react-router-dom";
import { Bell, ChevronRight, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";

function resolveBreadcrumbs(pathname: string): { label: string; href?: string }[] {
  if (pathname === "/") return [{ label: "Home" }];
  if (pathname.startsWith("/cars/")) {
    return [{ label: "Home", href: "/" }, { label: "Cars", href: "/cars" }, { label: "Details" }];
  }
  if (pathname === "/cars") return [{ label: "Home", href: "/" }, { label: "Cars" }];
  if (pathname.startsWith("/users/")) {
    return [{ label: "Home", href: "/" }, { label: "Users", href: "/users" }, { label: "Profile" }];
  }
  if (pathname === "/users") {
    return [{ label: "Home", href: "/" }, { label: "Users" }];
  }
  if (pathname.match(/^\/fuel-activity\/users\/[^/]+\/spend\/\d+\/\d+$/)) {
    return [
      { label: "Home", href: "/" },
      { label: "Fuel", href: "/gas-stations" },
      { label: "User activity", href: "/fuel-activity" },
      { label: "Yearly spend", href: pathname.replace(/\/\d+\/\d+$/, "") },
      { label: "Month" },
    ];
  }
  if (pathname.match(/^\/fuel-activity\/spend\/\d+\/\d+$/)) {
    return [
      { label: "Home", href: "/" },
      { label: "Fuel", href: "/gas-stations" },
      { label: "User activity", href: "/fuel-activity" },
      { label: "Yearly spend", href: "/fuel-activity/spend" },
      { label: "Month" },
    ];
  }
  if (pathname.match(/^\/fuel-activity\/users\/[^/]+\/spend\/\d+$/)) {
    return [
      { label: "Home", href: "/" },
      { label: "Fuel", href: "/gas-stations" },
      { label: "User activity", href: "/fuel-activity" },
      { label: "Yearly spend", href: pathname.replace(/\/\d+$/, "") },
      { label: "Breakdown" },
    ];
  }
  if (pathname.match(/^\/fuel-activity\/users\/[^/]+\/spend$/)) {
    return [
      { label: "Home", href: "/" },
      { label: "Fuel", href: "/gas-stations" },
      { label: "User activity", href: "/fuel-activity" },
      { label: "Yearly spend" },
    ];
  }
  if (pathname.match(/^\/fuel-activity\/spend\/\d+$/)) {
    return [
      { label: "Home", href: "/" },
      { label: "Fuel", href: "/gas-stations" },
      { label: "User activity", href: "/fuel-activity" },
      { label: "Yearly spend", href: "/fuel-activity/spend" },
      { label: "Breakdown" },
    ];
  }
  if (pathname === "/fuel-activity/spend") {
    return [
      { label: "Home", href: "/" },
      { label: "Fuel", href: "/gas-stations" },
      { label: "User activity", href: "/fuel-activity" },
      { label: "Yearly spend" },
    ];
  }
  if (pathname.startsWith("/fuel-activity/users/")) {
    return [
      { label: "Home", href: "/" },
      { label: "Fuel", href: "/gas-stations" },
      { label: "User activity", href: "/fuel-activity" },
      { label: "Profile" },
    ];
  }
  if (pathname.startsWith("/fuel-activity")) {
    return [
      { label: "Home", href: "/" },
      { label: "Fuel", href: "/gas-stations" },
      { label: "User activity" },
    ];
  }
  if (pathname.startsWith("/gas-station-brands/")) {
    return [
      { label: "Home", href: "/" },
      { label: "Fuel network", href: "/gas-stations" },
      { label: "Brand" },
    ];
  }
  if (pathname.startsWith("/gas-stations/")) {
    return [
      { label: "Home", href: "/" },
      { label: "Fuel network", href: "/gas-stations" },
      { label: "Details" },
    ];
  }
  if (pathname.startsWith("/gas-stations")) {
    return [{ label: "Home", href: "/" }, { label: "Fuel network" }];
  }
  if (pathname === "/businesses/connected-services") {
    return [
      { label: "Home", href: "/" },
      { label: "Businesses", href: "/businesses" },
      { label: "Approvals" },
    ];
  }
  if (pathname.startsWith("/businesses/")) {
    return [
      { label: "Home", href: "/" },
      { label: "Businesses", href: "/businesses" },
      { label: "Details" },
    ];
  }
  if (pathname === "/businesses") {
    return [{ label: "Home", href: "/" }, { label: "Businesses" }];
  }
  return [{ label: "Home", href: "/" }];
}

export function Header() {
  const { pathname } = useLocation();
  const breadcrumbs = resolveBreadcrumbs(pathname);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "sticky top-0 z-20 transition-all duration-300",
        scrolled
          ? "bg-white/70 shadow-[0_1px_0_rgba(22,51,0,0.05),0_8px_24px_rgba(22,51,0,0.05)] backdrop-blur-xl"
          : "bg-transparent"
      )}
    >
      <div className="flex items-center gap-4 px-8 py-3 lg:px-12">
        <nav className="hidden min-w-0 items-center gap-1 text-sm text-muted lg:flex">
          {breadcrumbs.map((crumb, i) => (
            <span key={`${crumb.label}-${i}`} className="flex items-center gap-1">
              {i > 0 ? <ChevronRight className="h-3.5 w-3.5" /> : null}
              {crumb.href ? (
                <Link to={crumb.href} className="transition-colors hover:text-foreground">
                  {crumb.label}
                </Link>
              ) : (
                <span className="font-semibold text-foreground">{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>

        <div className="relative hidden max-w-sm flex-1 lg:block lg:ml-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-light" />
          <input
            type="search"
            placeholder="Search cars, stations…"
            className="search-input focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Search"
          />
        </div>

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            className="hidden rounded-full px-4 py-2 text-sm font-semibold text-muted transition-colors hover:bg-canvas hover:text-foreground sm:block"
          >
            Docs
          </button>

          <button
            type="button"
            className="press-scale group relative flex h-9 w-9 items-center justify-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            aria-label="Notifications"
          >
            <Bell className="h-4 w-4 transition-transform duration-300 ease-[var(--ease-out-expo)] group-hover:rotate-12" />
            <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-accent" />
          </button>

          <div className="ml-1 flex h-8 w-8 items-center justify-center rounded-full bg-foreground text-[10px] font-bold text-accent transition-transform duration-300 ease-[var(--ease-out-expo)] hover:scale-110">
            KB
          </div>
        </div>
      </div>
    </header>
  );
}
